import React, { useEffect, useMemo, useState } from 'react';
import { Input } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { CUSTOM_CONTEXT_PATH, pathSetFromContextPathGroups } from '../../containers/Workflows/workflowContextPathOptions';
import ContextPathSyntaxFeedback from '../ContextPathSyntaxFeedback';

/**
 * Context path picker with optgroups (form, foreach iteration, brick outputs, cart).
 *
 * @param {object} props
 * @param {{ id: string, label: string, options: { path: string, label: string }[] }[]} props.groups
 * @param {string} props.value — raw stored path
 * @param {(next: string) => void} props.onChange
 * @param {Set<string>|undefined} props.knownPaths — union of suggested + extra (for matching pick vs custom)
 * @param {string} [props.className]
 * @param {object} [props.selectProps] — passed to the `<select>` (e.g. bsSize)
 * @param {object} [props.textInputProps] — passed to custom path `<Input type="text">`
 * @param {string} [props.customPlaceholder]
 * @param {boolean} [props.disabled]
 * @param {import('react').ReactNode} [props.footer] — e.g. redux-form error
 */
export default function ContextPathSelect({
    groups,
    value,
    onChange,
    knownPaths: known_paths_prop,
    className,
    selectProps,
    textInputProps,
    customPlaceholder = 'context.key',
    disabled = false,
    footer,
}) {
    const { t } = useTranslation();
    const [force_custom, setForceCustom] = useState(false);

    const suggested_set = useMemo(() => pathSetFromContextPathGroups(groups || []), [groups]);

    const known_paths = useMemo(() => {
        if (known_paths_prop && known_paths_prop.size > 0) {
            return known_paths_prop;
        }
        return suggested_set;
    }, [known_paths_prop, suggested_set]);

    const known_paths_key = useMemo(() => [...known_paths].sort().join('\u0001'), [known_paths]);

    useEffect(() => {
        setForceCustom(false);
    }, [known_paths_key]);

    const raw_path = String(value ?? '');
    const path_trim = raw_path.trim();
    const display_pick = force_custom
        ? CUSTOM_CONTEXT_PATH
        : !path_trim
            ? ''
            : known_paths.has(path_trim) && raw_path === path_trim
                ? path_trim
                : CUSTOM_CONTEXT_PATH;

    const on_select_change = (e) => {
        const v = e.target.value;
        if (v === '') {
            setForceCustom(false);
            onChange('');
            return;
        }
        if (v === CUSTOM_CONTEXT_PATH) {
            setForceCustom(true);
            return;
        }
        setForceCustom(false);
        onChange(v);
    };

    return (
        <div className={className}>
            <Input
                type="select"
                className="form-control"
                value={display_pick}
                onChange={on_select_change}
                disabled={disabled}
                {...(selectProps || {})}
            >
                <option value="">{t('workflows.graph.path_pick_placeholder')}</option>
                {(groups || []).map((g) => (
                    <optgroup key={g.id} label={g.label}>
                        {(g.options || []).map((o) => (
                            <option key={o.path} value={o.path}>
                                {o.label}
                            </option>
                        ))}
                    </optgroup>
                ))}
                <option value={CUSTOM_CONTEXT_PATH}>{t('workflows.graph.path_custom')}</option>
            </Input>
            {display_pick === CUSTOM_CONTEXT_PATH && (
                <>
                    <Input
                        type="text"
                        className="form-control mt-1"
                        value={value != null ? String(value) : ''}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={customPlaceholder}
                        disabled={disabled}
                        {...(textInputProps || {})}
                    />
                    <ContextPathSyntaxFeedback value={value != null ? String(value) : ''} knownPaths={known_paths} />
                </>
            )}
            {footer}
        </div>
    );
}
